import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { trackEvent } from "@/hooks/useBusinessData";

const NAV_LINKS = [
  { label: "Services",  to: "/services" },
  { label: "Photos",    to: "/photos" },
  { label: "Founder",   to: "/founder" },
  { label: "Reviews",   to: "/reviews" },
  { label: "Visit",     to: "/visit" },
];

export default function Nav({ config }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 32);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const isHome = location.pathname === "/";

  return (
    <>
      <motion.header
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.4, duration: 0.8, ease: [0.7, 0, 0.2, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled || !isHome
            ? "bg-black/85 backdrop-blur-md border-b border-white/10"
            : "bg-transparent"
        }`}
        data-testid="site-nav"
      >
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
          <Link
            to="/"
            data-testid="nav-logo-link"
            className="flex items-center gap-3 group"
          >
            <img
              src={config.logo_url}
              alt="She Cuts Barber Studio"
              className="h-10 w-10 md:h-11 md:w-11 rounded-full border border-white/20"
            />
            <div className="hidden sm:block leading-tight">
              <div className="font-serif text-[15px] tracking-wide">
                She Cuts
              </div>
              <div className="eyebrow text-[9px]">Barber Studio</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.to.replace("/", "")}`}
                className={({ isActive }) =>
                  `line-hover text-[13px] tracking-[0.15em] uppercase transition-colors ${
                    isActive ? "text-white" : "text-zinc-400 hover:text-white"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <a
              href={`tel:${config.phone_tel}`}
              onClick={() => trackEvent("call_clicked", { source: "nav" })}
              data-testid="nav-call-button"
              className="btn-ghost"
            >
              <Phone className="w-3.5 h-3.5" strokeWidth={1.5} />
              Call
            </a>
            <Link
              to="/book"
              onClick={() => trackEvent("book_clicked", { source: "nav" })}
              data-testid="nav-book-button"
              className="btn-primary"
            >
              Book Now
            </Link>
          </div>

          <button
            className="lg:hidden text-white p-2"
            onClick={() => setOpen(true)}
            aria-label="Open menu"
            data-testid="nav-mobile-open"
          >
            <Menu className="w-6 h-6" strokeWidth={1.4} />
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-[70] bg-black"
            data-testid="mobile-menu"
          >
            <div className="max-w-[1600px] mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
              <img
                src={config.logo_url}
                alt=""
                className="h-11 w-11 rounded-full border border-white/20"
              />
              <button
                className="text-white p-2"
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                data-testid="nav-mobile-close"
              >
                <X className="w-6 h-6" strokeWidth={1.4} />
              </button>
            </div>
            <div className="px-6 md:px-10 pt-10 pb-16 flex flex-col gap-7 h-[calc(100vh-4rem)] overflow-y-auto">
              {NAV_LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.1 + i * 0.05, duration: 0.5 }}
                >
                  <NavLink
                    to={l.to}
                    className="font-serif text-4xl sm:text-5xl leading-none tracking-tight text-white"
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <div className="mt-auto space-y-3 pt-8 border-t border-white/10">
                <a
                  href={`tel:${config.phone_tel}`}
                  onClick={() => trackEvent("call_clicked", { source: "mobile_menu" })}
                  className="btn-ghost w-full justify-center"
                >
                  <Phone className="w-3.5 h-3.5" strokeWidth={1.5} /> {config.phone}
                </a>
                <Link
                  to="/book"
                  onClick={() => trackEvent("book_clicked", { source: "mobile_menu" })}
                  className="btn-primary w-full justify-center"
                >
                  Book Now
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
